import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { SectionCard } from '../../../components/design-system/admin';
import { SettingsToggleRow } from '../../../components/design-system/common';
import { SnapFindSettings, useNetworkApi } from '../../../common/hooks/use-network-api.hook';
import { readApiError } from '../../../common/utils/api-error';

/** How often the card re-reads progress while the catalogue is being fingerprinted. */
const POLL_MS = 5000;

/**
 * Snap & Find for this shop: one switch, where fingerprinting the catalogue
 * stands, and what shoppers did with it in the last 30 days.
 *
 * Switching it on shows the photo button to the shop's shoppers straight
 * away (the visualizer, and the assistant when it is on) and starts
 * fingerprinting; the button stays hidden until at least one product can be
 * matched, so a shopper never gets one that can only fail.
 */
export const SnapFindCard: React.FC = () => {
    const { t, i18n } = useTranslation();
    const api = useNetworkApi();
    const [settings, setSettings] = useState<SnapFindSettings | null>(null);
    const [failed, setFailed] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            setSettings(await api.snapFind());
            setFailed(false);
        } catch {
            setFailed(true);
        }
    }, [api]);

    useEffect(() => {
        load();
    }, [load]);

    const indexing = settings?.enabled ? settings.indexing : undefined;
    useEffect(() => {
        if (!indexing) return undefined;
        const timer = window.setTimeout(load, POLL_MS);
        return () => window.clearTimeout(timer);
    }, [indexing, load]);

    const save = async (enabled: boolean) => {
        setSaving(true);
        try {
            setSettings(await api.updateSnapFind(enabled));
            toast.success(t(enabled ? 'NETWORK_RETAILER.SNAP.SAVED_ON' : 'NETWORK_RETAILER.SNAP.SAVED_OFF'));
        } catch (e) {
            toast.error((await readApiError(e)).message || t('NETWORK_RETAILER.ERROR'));
        } finally {
            setSaving(false);
        }
    };

    const number = (n: number) => n.toLocaleString(i18n.language);
    const status = !settings?.enabled
        ? t('NETWORK_RETAILER.SNAP.STATUS_OFF')
        : indexing
          ? t('NETWORK_RETAILER.SNAP.STATUS_INDEXING', { embedded: number(indexing.embedded), total: number(indexing.total) })
          : settings.available
            ? t('NETWORK_RETAILER.SNAP.STATUS_READY')
            : t('NETWORK_RETAILER.SNAP.STATUS_UNAVAILABLE');

    return (
        <SectionCard title={t('NETWORK_RETAILER.SNAP.TITLE')} description={t('NETWORK_RETAILER.SNAP.SUBTITLE')} testId="network-snap-find">
            <div className="flex flex-col gap-6">
                <SettingsToggleRow
                    id="network-snap-find"
                    label={t('NETWORK_RETAILER.SNAP.TOGGLE')}
                    description={t('NETWORK_RETAILER.SNAP.TOGGLE_HINT')}
                    checked={settings?.enabled ?? false}
                    disabled={!settings || saving}
                    onCheckedChange={save}
                />

                {failed ? (
                    <p className="text-sm text-muted-foreground">{t('NETWORK_RETAILER.ERROR')}</p>
                ) : (
                    settings && (
                        <div className="flex flex-col gap-5 border-t pt-5">
                            <p className="text-sm" data-testid="network-snap-find-status">
                                {status}
                            </p>
                            <dl className="grid grid-cols-3 gap-4">
                                <Stat label={t('NETWORK_RETAILER.SNAP.SEARCHES')} value={number(settings.last30Days.searches)} />
                                <Stat label={t('NETWORK_RETAILER.SNAP.MATCHED')} value={number(settings.last30Days.matched)} />
                                <Stat label={t('NETWORK_RETAILER.SNAP.CHOSEN')} value={number(settings.last30Days.chosen)} />
                            </dl>
                            <p className="text-xs text-muted-foreground">{t('NETWORK_RETAILER.SNAP.LAST_30_DAYS')}</p>
                        </div>
                    )
                )}
            </div>
        </SectionCard>
    );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div>
        <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
        <dd className="mt-1 text-2xl font-medium tabular-nums">{value}</dd>
    </div>
);
